import React from 'react';

interface Recipe {
  name: string;
  ingredients: { [key: string]: number };
  output: { [key: string]: number };
  unlocked: boolean;
  type: string;
  requires?: string[];
}

interface RecipeCardProps {
  recipe: Recipe;
  onCraft: (recipe: Recipe) => void;
  inventory: { [key: string]: number };
  addFloatingText: (text: string, x: number, y: number, color: string) => void;
}

const getEmoji = (resource: string) => {
  const emojiMap: { [key: string]: string } = {
    wood: "🪵",
    rocks: "🪨",
    clay: "🟤",
    tools: "🔨",
    bricks: "🧱",
    flour: "❄️",
    millStructure: "⚙️",
    ovenStructure: "🏭",
    silo: "🏗️",
    marketStall: "🏪",
    wheat: "🌾",
    bread: "🍞"
  };
  return emojiMap[resource] || "";
};

const RecipeCard: React.FC<RecipeCardProps> = ({ recipe, onCraft, inventory, addFloatingText }) => {
  const canCraft = Object.entries(recipe.ingredients).every(
    ([resource, amount]) => inventory[resource] >= amount
  ) && !(
    (recipe.output.millStructure && inventory.millStructure > 0) ||
    (recipe.output.ovenStructure && inventory.ovenStructure > 0) ||
    (recipe.output.silo && inventory.silo > 0) ||
    (recipe.output.marketStall && inventory.marketStall > 0)
  );

  const requirementsMet = !recipe.requires || recipe.requires.every(req => {
    switch (req) {
      case "Wheat Mill": return inventory.millStructure > 0;
      case "Baker's Oven": return inventory.ovenStructure > 0;
      case "Storage Silo": return inventory.silo > 0;
      case "Market Stall": return inventory.marketStall > 0;
      case "Basic Tools": return inventory.tools > 0;
      default: return true;
    }
  });

  return (
    <div className={`recipe-card ${canCraft ? 'available' : 'unavailable'} ${!requirementsMet ? 'locked' : ''}`}>
      <h3>{recipe.name}</h3>
      {!requirementsMet && (
        <div className="requirements">
          Required structures: 
          {recipe.requires?.map(req => (
            <span 
              key={req} 
              className={
                (() => {
                  switch (req) {
                    case "Wheat Mill": return inventory.millStructure > 0;
                    case "Baker's Oven": return inventory.ovenStructure > 0;
                    case "Storage Silo": return inventory.silo > 0;
                    case "Market Stall": return inventory.marketStall > 0;
                    case "Basic Tools": return inventory.tools > 0;
                    default: return false;
                  }
                })() ? 'has-requirement' : 'missing-requirement'
              }
            >
              {req}
            </span>
          ))}
        </div>
      )}
      <div className="recipe-ingredients">
        <span className="label">Requires:</span>
        <div className="resource-list">
          {Object.entries(recipe.ingredients).map(([item, amount]) => (
            <span 
              key={item} 
              className={`resource-item ${inventory[item] >= amount ? 'has-resources' : 'missing-resources'}`}
            >
              {getEmoji(item)} {amount} {item} 
              <span className="inventory-count">
                (have: {inventory[item]})
              </span>
            </span>
          ))}
        </div>
      </div>
      <div className="recipe-output">
        <span className="label">Creates:</span>
        <div className="resource-list">
          {Object.entries(recipe.output).map(([item, amount]) => (
            <span key={item} className="resource-item">
              {getEmoji(item)} {amount} {
                item === 'millStructure' ? 'Wheat Mill' :
                item === 'ovenStructure' ? "Baker's Oven" :
                item === 'silo' ? 'Storage Silo' :
                item === 'marketStall' ? 'Market Stall' :
                item
              }
            </span>
          ))}
        </div>
      </div>
      <button 
        className="craft-button"
        onClick={(e) => {
          onCraft(recipe);
          // Add floating text for ingredients consumed
          Object.entries(recipe.ingredients).forEach(([item, amount], index) => {
            addFloatingText(
              `-${amount} ${getEmoji(item)}`, 
              e.clientX - 20, 
              e.clientY - 20 + (index * 20), 
              '#FF4444'
            );
          });
          // Add floating text for items created
          Object.entries(recipe.output).forEach(([item, amount], index) => {
            addFloatingText(
              `+${amount} ${getEmoji(item)}`, 
              e.clientX + 20, 
              e.clientY - 20 + (index * 20), 
              '#4CAF50'
            );
          });
        }}
        disabled={!canCraft || !requirementsMet}
      >
        {recipe.output.millStructure || recipe.output.ovenStructure || 
         recipe.output.silo || recipe.output.marketStall 
          ? "Place Structure" 
          : "Craft"}
      </button>
    </div>
  );
};

export default React.memo(RecipeCard);

